import { Box, useTheme, alpha } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import type { RoadmapPhaseStatus } from "../../api/client";

interface StatusDotProps {
  status: RoadmapPhaseStatus;
  size?: "sm" | "md";
}

// Status colors map to design tokens:
//   done    → green fill (+ check at md size)
//   current → coral fill + soft coral ring outside the bounding box
//   upcoming→ hollow circle with a 30%-opacity body-text border
// Spec §5.1, §6.1, §6.4. Border color is not theme.divider (which is 8% and
// invisible at small sizes); we use 30% of the primary text color for
// adequate contrast on white + dark backgrounds.
export default function StatusDot({ status, size = "md" }: StatusDotProps) {
  const theme = useTheme();
  const px = size === "sm" ? 8 : 28;
  const showCheck = status === "done" && size === "md";

  const ringOpacity = 0.45;
  const ringWidth = size === "sm" ? 2 : 4;
  const ring =
    status === "current"
      ? `0 0 0 ${ringWidth}px ${alpha(
          theme.palette.secondary.main,
          ringOpacity,
        )}`
      : "none";

  const bg =
    status === "done"
      ? theme.palette.success.main
      : status === "current"
        ? theme.palette.secondary.main
        : "transparent";

  return (
    <Box
      aria-hidden="true"
      sx={{
        width: px,
        height: px,
        borderRadius: "50%",
        bgcolor: bg,
        border: status === "upcoming" ? "1.5px solid" : "none",
        borderColor: alpha(theme.palette.text.primary, 0.3),
        boxShadow: ring,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {showCheck && (
        <CheckIcon
          sx={{ fontSize: 16, color: "common.white", strokeWidth: 3 }}
        />
      )}
    </Box>
  );
}
